import { useEffect, useState } from "react";

import { Drawer, Flex, Select, Stack } from "@mantine/core";
import { useForm } from "@mantine/form";

import { zodResolver } from "mantine-form-zod-resolver";

import { DisabledReasonButton, NO_CHANGES_REASON } from "@/components/DisabledReason";
import { TextWrapper } from "@/components/TextWrapper";
import { NO_DISPLAY, YES_DISPLAY } from "@/intake-forms/constants/yes-no-display";
import { replaceFormSnapshotForDrawerDismiss } from "@/settings/utils/replace-form-snapshot-for-drawer-dismiss";

import { emptyStatementSettings, StatementSettingsField } from "../utils/statement-settings";
import {
  StatementSettingsFormSchema,
  StatementSettingsFormType,
} from "../zod-schemas/statement-settings";

const YES_NO_OPTIONS = [YES_DISPLAY, NO_DISPLAY];

interface StatementSettingsDrawerProps {
  opened: boolean;
  onClose: () => void;
  fields: StatementSettingsField[];
  settings: StatementSettingsFormType;
  onSave: (values: StatementSettingsFormType) => Promise<boolean>;
}

export default function StatementSettingsDrawer({
  opened,
  onClose,
  fields,
  settings,
  onSave,
}: StatementSettingsDrawerProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<StatementSettingsFormType>({
    initialValues: settings,
    // The practice may save with some answers left unset; the per-statement Authorizations drawer
    // still requires all four. See PE-5416.
    validate: zodResolver(StatementSettingsFormSchema),
  });
  const { setValues, resetDirty } = form;

  // Re-seed the form whenever the drawer reopens so it shows the latest saved answers.
  useEffect(() => {
    if (opened) {
      setValues(settings);
      resetDirty(settings);
    }
  }, [opened, settings, setValues, resetDirty]);

  const closeDrawer = () => {
    replaceFormSnapshotForDrawerDismiss(form, emptyStatementSettings());
    onClose();
  };

  // Ignore user-initiated dismissals while a save is in flight.
  const handleDismiss = () => {
    if (isSubmitting) return;
    closeDrawer();
  };

  const handleSubmit = async (values: StatementSettingsFormType) => {
    try {
      setIsSubmitting(true);
      const saved = await onSave(values);
      if (saved) closeDrawer();
    } catch (error) {
      console.error("Statement settings save failed, keeping the drawer open:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const hasChanges = form.isDirty();

  return (
    <Drawer.Root
      trapFocus
      size={500}
      opened={opened}
      position="right"
      closeOnEscape={false}
      onClose={handleDismiss}
      closeOnClickOutside={false}
    >
      <Drawer.Overlay />
      <Drawer.Content bg="white">
        <form noValidate onSubmit={form.onSubmit(handleSubmit)}>
          <Drawer.Header h={64} px={24} bg="gray.0">
            <Flex w="100%" gap="md" align="center" justify="space-between">
              <Flex gap="md" align="center" style={{ flex: 1 }}>
                <Drawer.CloseButton
                  disabled={isSubmitting}
                  data-testid="statement-settings-close"
                  aria-label="Close Statement Settings Drawer"
                />
                <Drawer.Title style={{ flex: 1 }}>
                  <TextWrapper fz="lg" c="dark.9" variant="bold">
                    Statement Settings
                  </TextWrapper>
                </Drawer.Title>
              </Flex>
              <DisabledReasonButton
                type="submit"
                loading={isSubmitting}
                data-testid="statement-settings-save"
                disabledReason={hasChanges ? undefined : NO_CHANGES_REASON}
              >
                Save
              </DisabledReasonButton>
            </Flex>
          </Drawer.Header>
          <Drawer.Body p={24}>
            <Stack gap={24}>
              {fields.map((field) => (
                <Select
                  key={field.linkId}
                  label={field.label}
                  placeholder="Select"
                  allowDeselect={false}
                  data={YES_NO_OPTIONS}
                  data-testid={`statement-settings-${field.linkId}`}
                  {...form.getInputProps(field.linkId)}
                />
              ))}
            </Stack>
          </Drawer.Body>
        </form>
      </Drawer.Content>
    </Drawer.Root>
  );
}
